// TODO- old file need to delete when PRD-113 released flag: checkInRevamped
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import Dialog, { DialogContent, DialogActions } from "components/common/Dialog";
import Button from "components/common/Button";
import TimeRangeSelector from "components/CheckInCheckOut/TimeRangeSelector";
import ShiftSelectionSuccessModal from "components/CheckInCheckOut/ShiftSelectionSuccessModal";
import { AlertType } from "components/common/Snackbar";
import { setEmployeeSchedule, clockEntry } from "services/CheckInCheckOut/CheckInAndBreaks";
import { showSnackbar } from "store/actions/Utility";
import "./SelectShiftModal.scss";
import { useFlags } from "launchdarkly-react-client-sdk";

function SelectShiftModal({ open, onClose, onConfirm }) {
  const reduxDispatch = useDispatch();
  const { checkInRevamped } = useFlags();
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [showNextDayLabel, setShowNextDayLabel] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shiftData, setShiftData] = useState(null);
  const [clockEntryErrorCode, setClockEntryErrorCode] = useState(undefined);

  useEffect(() => {
    if (startTime && endTime) {
      const startHours = startTime.getHours();
      const endHours = endTime.getHours();
      const startMinutes = startTime.getMinutes();
      const endMinutes = endTime.getMinutes();

      const startTotalMinutes = startHours * 60 + startMinutes;
      const endTotalMinutes = endHours * 60 + endMinutes;

      setShowNextDayLabel(endTotalMinutes < startTotalMinutes);
    } else {
      setShowNextDayLabel(false);
    }
  }, [startTime, endTime]);

  const isCurrentTimeInShiftRange = (start, end) => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const startMinutes = start.getHours() * 60 + start.getMinutes();
    const endMinutes = end.getHours() * 60 + end.getMinutes();
    const threeHoursInMinutes = 3 * 60; // 180 minutes

    // Calculate early clock-in threshold (3 hours before shift start)
    // But cap it at midnight (0 minutes) to avoid previous day confusion
    const earlyClockInMinutes = Math.max(0, startMinutes - threeHoursInMinutes);

    // Handle overnight shifts (end time is on next day)
    if (endMinutes < startMinutes) {
      // Shift spans midnight
      // Allow clock-in from early threshold until shift end (next day)
      return currentMinutes >= earlyClockInMinutes || currentMinutes <= endMinutes;
    }

    // Normal shift within same day
    return currentMinutes >= earlyClockInMinutes && currentMinutes <= endMinutes;
  };

  const formatTimestamp = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const handleConfirm = async () => {
    if (startTime && endTime) {
      setIsSubmitting(true);

      try {
        const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
        const employeeId = loggedInUser?.userId;

        if (!employeeId) {
          reduxDispatch(
            showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
          );
          setIsSubmitting(false);
          return;
        }

        // Get user's timezone
        // const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timezone = "America/Lima";
        // Format times as HH:mm
        const formatTime = (date) => {
          const hours = date.getHours().toString().padStart(2, "0");
          const minutes = date.getMinutes().toString().padStart(2, "0");
          return `${hours}:${minutes}`;
        };

        const scheduleData = {
          startTime: formatTime(startTime),
          endTime: formatTime(endTime),
          timezone,
        };

        // Call schedule API
        await setEmployeeSchedule(employeeId, scheduleData);

        const shouldClockIn = isCurrentTimeInShiftRange(startTime, endTime);
        let actuallyClocked = shouldClockIn;

        // If current time is within shift range, also call clock entry API
        if (shouldClockIn) {
          try {
            const timestamp = formatTimestamp(new Date());
            await clockEntry(
              employeeId,
              {
                action: "check_in",
                timestamp,
              },
              checkInRevamped
            );
            setClockInTime(new Date());
          } catch (clockError) {
            // Check if it's a leave/weekend/holiday error
            if (
              clockError.errorCode === "leave" ||
              clockError.errorCode === "weekend" ||
              clockError.errorCode === "holiday"
            ) {
              // Schedule was saved but clock-in failed due to leave/weekend/holiday
              actuallyClocked = false;
              setClockInTime(null);
              setClockEntryErrorCode(clockError.errorCode);
            } else {
              // Other errors should be shown in snackbar
              throw clockError;
            }
          }
        } else {
          setClockInTime(null);
        }

        setIsClockedIn(actuallyClocked);
        setShiftData({
          startTime,
          endTime,
          isNextDay: showNextDayLabel,
          clockedIn: actuallyClocked,
          errorCode: clockEntryErrorCode,
        });
        setShowSuccessModal(true);
      } catch (error) {
        reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    setStartTime(null);
    setEndTime(null);
    setShowNextDayLabel(false);
    setClockEntryErrorCode(undefined);
    onClose();
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);

    if (shiftData) {
      onConfirm(shiftData);
      setShiftData(null);
    }

    handleClose();
  };

  return (
    <>
      <Dialog
        open={open && !showSuccessModal}
        onClose={handleClose}
        width="688px"
        title={
          <div className="select-shift-modal__title-section">
            <div className="select-shift-modal__icon">
              <ScheduleOutlinedIcon />
            </div>
            <div className="select-shift-modal__title-content">
              <h2 className="heading-h2-semibold">Confirm your shift timings</h2>
            </div>
            <p className="select-shift-modal__description para-body-m-regular">
              Before we continue, please confirm the shift timings you've agreed on with your client. Don't worry —
              you'll only need to do this once!
            </p>
          </div>
        }
        content={
          <DialogContent>
            <div className="select-shift-modal__content">
              <TimeRangeSelector
                startTime={startTime}
                endTime={endTime}
                onStartTimeChange={setStartTime}
                onEndTimeChange={setEndTime}
                showNextDayLabel={showNextDayLabel}
                helperText="Please enter all times in your local timezone."
                startLabel="Start shift time"
                endLabel="End shift time"
                nextDayText="Next day"
              />
            </div>
          </DialogContent>
        }
        actions={
          <DialogActions
            action1={<Button label="Cancel" size="large" variant="text" onClick={handleClose} />}
            action2={
              <Button
                label={
                  startTime && endTime && isCurrentTimeInShiftRange(startTime, endTime)
                    ? "Confirm and Clock-in"
                    : "Confirm Shift Time"
                }
                size="large"
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                isDisabled={!startTime || !endTime || isSubmitting}
              />
            }
          />
        }
        size="sm"
      />

      <ShiftSelectionSuccessModal
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        shiftStartTime={startTime}
        shiftEndTime={endTime}
        clockInTime={clockInTime}
        isClockedIn={isClockedIn}
        errorCode={clockEntryErrorCode}
      />
    </>
  );
}

SelectShiftModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default SelectShiftModal;
